<template>
  <RevForm
    :id
    class="mt-12"
    :initial-values
    :name="id"
    :validate
    @submit="handleSubmit"
  >
    <template #default="{ values, errors }">
      <RevInputText
        id="owner"
        v-model="values.owner"
        class="mb-20"
        :description="i18n(translations.ownerDescription)"
        :disabled
        :error="errors.owner"
        :label="i18n(translations.ownerLabel)"
      />
      <RevInputText
        id="email"
        v-model="values.email"
        class="mb-20"
        :description="i18n(translations.emailDescription)"
        :disabled
        :error="errors.email"
        :label="i18n(translations.emailLabel)"
      />
      <RevInputText
        id="account-number"
        v-model="values.accountNumber"
        class="mb-20"
        :description="i18n(translations.accountNumberDescription)"
        :disabled
        :error="errors.accountNumber"
        :label="i18n(translations.accountNumber)"
      />
      <RevInputText
        id="sort-code"
        v-model="values.sortCode"
        class="mb-20"
        :description="i18n(translations.sortCodeDescription)"
        :disabled
        :error="errors.sortCode"
        :label="i18n(translations.sortCode)"
      />
    </template>
  </RevForm>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import {
  RevForm,
  makeValidate,
  maxLength,
  minLength,
  required,
} from '@ds/components/Form'
import { RevInputText } from '@ds/components/InputText'

import { usePaymentAuthorize } from '../../../../api/postPaymentAuthorize/usePaymentAuthorize.api'
import { getRedirectionToPaymentResult } from '../../../form-common/helpers/getRedirection'
import type {
  PaymentFormAdapterEmits,
  PaymentFormAdapterProps,
  SubmitSuccessData,
} from '../../../form-common/types/PaymentFormAdapter'
import { PaymentSubmitError } from '../../../form-common/types/PaymentSubmitError'

import translations from './BacsAdapter.translations'

const BACS_NUMBER_LENGTH = 8
const SORT_CODE_LENGTH = 6

const props = defineProps<PaymentFormAdapterProps>()
const emit = defineEmits<PaymentFormAdapterEmits>()

const i18n = useI18n()
const postPaymentAuthorize = usePaymentAuthorize()

type Values = {
  accountNumber: string
  sortCode: string
  owner: string

  // TODO [PAYIN-5118] Email is no longer needed as the seller one is used. We need to remove the field.
  email: string
}

const handleSubmit = async (values: Values) => {
  emit('submit-start')
  try {
    const { paymentId } = await props.createPayment(values)
    const { accountNumber, sortCode, owner } = values

    await postPaymentAuthorize({
      pathParams: { paymentId },
      body: {
        ownerName: owner,
        bankAccount: {
          number: accountNumber,
          locationID: sortCode,
        },
      },
    })

    emit('submit-success', {
      paymentId,
      redirection: getRedirectionToPaymentResult(paymentId),
    } satisfies SubmitSuccessData)
  } catch (error) {
    emit('submit-error', PaymentSubmitError.fromAnyError(error))
  }
}

const initialValues = computed(
  (): Values => ({
    accountNumber: '',
    sortCode: '',
    owner: '',
    email: '',
  }),
)

const validate = makeValidate<Values>({
  accountNumber: [
    required(i18n(translations.required)),
    maxLength(
      BACS_NUMBER_LENGTH,
      i18n(translations.accountNumberError, { length: BACS_NUMBER_LENGTH }),
    ),
    minLength(
      BACS_NUMBER_LENGTH,
      i18n(translations.accountNumberError, { length: BACS_NUMBER_LENGTH }),
    ),
  ],
  sortCode: [
    required(i18n(translations.required)),
    maxLength(
      SORT_CODE_LENGTH,
      i18n(translations.sortCodeError, { length: SORT_CODE_LENGTH }),
    ),
    minLength(
      SORT_CODE_LENGTH,
      i18n(translations.sortCodeError, { length: SORT_CODE_LENGTH }),
    ),
  ],
  owner: [required(i18n(translations.required))],
  email: [required(i18n(translations.required))],
})
</script>
